import { ChangeEvent, useState } from 'react';
import { Box, FileUploader } from '@nimbus-ds/components';
import { CATALOG_UPLOAD_VIDEOS } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { AlertImagesValidation, useResponsive } from 'commons/components';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import FileDraggable from 'domains/Catalog/Products/pages/components/FileDraggable';
import { useImageLoader } from 'domains/Catalog/Products/pages/hooks';
import { MediaGalleryState } from '../../ImageGallery';
import AlertMaxImages from '../AlertMaxImages/AlertMaxImages';

import './MediaDropArea.scss';

interface MediaDropAreaProps {
  videoQuantity: number;
  allowUploadVideos: boolean;
  onUploadImages: (images: MediaGalleryState[]) => void;
  customText?: string;
  countItems: number;
  maxItemsToLoad?: number;
}

function MediaDropArea({
  videoQuantity,
  allowUploadVideos,
  onUploadImages,
  customText,
  countItems,
  maxItemsToLoad,
}: Readonly<MediaDropAreaProps>): JSX.Element {
  const t = useTranslationCatalog('products.detail');

  const { isDesktop } = useResponsive();
  const [hasUploadVideos] = useHasTags([CATALOG_UPLOAD_VIDEOS]);
  const [timesNewElement, setTimesNewElement] = useState(1);

  const step1 =
    hasUploadVideos && allowUploadVideos ? 'photosAndVideo' : 'onlyPhotos';
  const step2 = isDesktop ? 'dragAndDropOrSelect' : 'select';
  const text = customText || t(`${step1}.${step2}`);

  const {
    itemsExceededLimit,
    imagesWithBadType,
    imagesExceededSize,
    videosExceededSize,
    videosExceededQuantity,
    acceptTypeFile,
    setItemsExceededLimit,
    setImagesExceededSize,
    setImagesWithBadType,
    setVideosExceededSize,
    setVideosExceededQuantity,
    handleOnchange,
  } = useImageLoader({
    onUploadImages,
    videoQuantity,
    allowUploadVideos,
    countItems,
    maxItemsToLoad,
  });

  const handleOnDrop = (files: File[]) => {
    setItemsExceededLimit(false);
    setImagesExceededSize([]);
    setImagesWithBadType([]);
    setVideosExceededSize([]);
    setVideosExceededQuantity(false);
    handleOnchange(files);
  };

  const handleChangeFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    setTimesNewElement((value) => value + 1);
    const files = event.target.files;
    if (files) {
      handleOnchange(Array.from(files));
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="2-5">
      {itemsExceededLimit && !!maxItemsToLoad && (
        <AlertMaxImages
          maxImagesToLoad={maxItemsToLoad}
          onRemove={() => setItemsExceededLimit(false)}
        />
      )}
      <AlertImagesValidation filenames={imagesWithBadType} validation="type" />
      <AlertImagesValidation filenames={imagesExceededSize} validation="size" />
      <AlertImagesValidation
        filenames={videosExceededSize}
        validation="videoSize"
      />
      {videosExceededQuantity && (
        <AlertImagesValidation filenames={['']} validation="videoQuantity" />
      )}
      {((!!maxItemsToLoad && countItems < maxItemsToLoad) ||
        !maxItemsToLoad) && (
        <FileDraggable onDropFiles={handleOnDrop}>
          <div className="stratus--media-drop-area opacable">
            <FileUploader
              key={timesNewElement}
              // This key allows re-selecting the same file (otherwise onChange won't fire).
              // Assigning a new key on each onChange forces a re-render.
              placeholder={text}
              aspectRatio="none"
              height="88px"
              onChange={handleChangeFiles}
              accept={acceptTypeFile}
              multiple
            />
          </div>
        </FileDraggable>
      )}
    </Box>
  );
}

export default MediaDropArea;
